import type { ArenaInteractionsHandler } from '../arenas/interactions';
import {
  BASE_HP,
  DEFAULT_DODGE_COUNTER_CHANCE,
  HP_PER_POINT,
  KICK_CHANCE,
  KICK_DAMAGE_MULT,
  KICK_LAUNCHES,
  ROUNDS_PER_BATTLE,
} from '../data/constants';
import { computeBaseDamage, computeDodgeChance, computeMaxHP } from './DamageCalculator';
import { rollInitiative } from './Initiative';
import {
  resolveAttackSkill,
  resolveBlockSkill,
  resolveBuffSkill,
  resolveRepairSkill,
} from './SkillResolver';
import type {
  Actor,
  AttackSkill,
  BattleEvent,
  BattleSequence,
  BlockSkill,
  BuffSkill,
  CharacterConfig,
  CombatantState,
  DefaultAttack,
  DefenderReaction,
  SkillRegistry,
} from './types';

export type ResolveBattleParams = {
  p1: CharacterConfig;
  p2: CharacterConfig;
  skills: SkillRegistry;
  rng?: () => number;
  // Override the default ROUNDS_PER_BATTLE (handy for tests).
  rounds?: number;
  // Optional arena interaction handler. Lets the active arena react to attack
  // outcomes (embed objects, trigger explosions on tagged skills, etc).
  arenaInteractions?: ArenaInteractionsHandler;
};

export function createCombatant(config: CharacterConfig): CombatantState {
  const maxHP = computeMaxHP(config.stats.health, BASE_HP, HP_PER_POINT);
  return { config, currentHP: maxHP, maxHP, pendingAttackMult: 1 };
}

// Pre-resolved outcome of one defender's reaction to one incoming attack.
// Holds everything the engine needs to emit events + apply damage in order.
type AttackResolution = {
  attackSkill: AttackSkill | null;
  blockSkill: BlockSkill | null;
  // Which default attack was rolled (punch/kick). Only meaningful when
  // attackSkill is null; 'punch' otherwise (renderer ignores it for skills).
  defaultAttack: DefaultAttack;
  reaction: DefenderReaction;
  damage: number;       // damage to defender (post block reduction; 0 on dodge)
  reduced: number;      // damage absorbed by block (for the block event payload)
  counter: boolean;     // does defender hit back this turn
  counterKick: boolean; // counter strike is a kick (rolled only when countering)
  counterDamage: number;// damage to attacker if counter is true
  // Defender buff that triggered on a successful dodge (ADBC). Boosts the
  // counter damage above; engine emits its skill_trigger before the counter.
  defenderBuffSkill: BuffSkill | null;
};

export function resolveBattle(params: ResolveBattleParams): BattleSequence {
  const { skills } = params;
  const rng = params.rng ?? Math.random;
  const totalRoundsCap = params.rounds ?? ROUNDS_PER_BATTLE;
  const arenaInteractions = params.arenaInteractions;

  const states: Record<Actor, CombatantState> = {
    p1: createCombatant(params.p1),
    p2: createCombatant(params.p2),
  };

  // Persist across rounds within a single battle. Stays null when no handler
  // is provided so the engine path is identical to before.
  const arenaState: unknown = arenaInteractions?.initialState() ?? null;

  const events: BattleEvent[] = [];
  let roundsPlayed = 0;

  for (let i = 0; i < totalRoundsCap; i++) {
    const round = i + 1;
    roundsPlayed = round;

    const firstActor = rollInitiative(
      states.p1.config.stats.agility,
      states.p2.config.stats.agility,
      rng(),
    );
    const secondActor: Actor = firstActor === 'p1' ? 'p2' : 'p1';

    events.push({ type: 'round_start', round, firstActor });
    events.push({ type: 'action_start', actor: firstActor });
    events.push({ type: 'action_start', actor: secondActor });

    for (const actor of [firstActor, secondActor] as const) {
      const opponent: Actor = actor === 'p1' ? 'p2' : 'p1';
      // Skip if either died from an earlier swing this round.
      if (states[actor].currentHP <= 0 || states[opponent].currentHP <= 0) continue;
      resolveAttack(actor, opponent, states, skills, rng, events, arenaInteractions, arenaState);
    }

    events.push({ type: 'action_end', actor: firstActor });
    events.push({ type: 'action_end', actor: secondActor });
    events.push({ type: 'round_end', round });

    if (states.p1.currentHP <= 0 || states.p2.currentHP <= 0) break;
  }

  const winner = decideWinner(states);
  events.push({ type: 'battle_end', winner });

  return {
    events,
    winner,
    totalRounds: roundsPlayed,
    players: {
      p1: { name: states.p1.config.name, maxHP: states.p1.maxHP },
      p2: { name: states.p2.config.name, maxHP: states.p2.maxHP },
    },
  };
}

// Pre-resolves the entire attack (rolls everything) THEN emits events in
// animation-friendly order. Pre-resolution lets us put defenderReaction on the
// attack_start event so the renderer can schedule defender's animation
// reactively without lookahead.
function resolveAttack(
  attacker: Actor,
  defender: Actor,
  states: Record<Actor, CombatantState>,
  skills: SkillRegistry,
  rng: () => number,
  events: BattleEvent[],
  arenaInteractions: ArenaInteractionsHandler | undefined,
  arenaState: unknown,
): void {
  const attackerState = states[attacker];
  const defenderState = states[defender];

  // Repair check FIRST - rolled before any attack resolution. If a repair
  // skill triggers the actor spends its turn self-healing: no attack, no
  // defender reaction. Only consumes rng for repair-kind skills the actor has
  // (characters without repair skills are unaffected).
  const repairSkill = resolveRepairSkill(attackerState, skills, rng);
  // at full HP a repair proc is ignored and the turn continues as a normal
  // attack (rng consumption stays identical, only the outcome changes)
  if (repairSkill && attackerState.currentHP < attackerState.maxHP) {
    events.push({ type: 'skill_trigger', actor: attacker, skillId: repairSkill.id });
    const before = attackerState.currentHP;
    const healed = Math.floor(attackerState.maxHP * repairSkill.healPercent);
    attackerState.currentHP = Math.min(attackerState.maxHP, before + healed);
    events.push({
      type: 'heal',
      actor: attacker,
      amount: attackerState.currentHP - before,
      newHP: attackerState.currentHP,
    });
    return;
  }

  // Buff roll: AFTER repair (we're not healing), BEFORE the attack it boosts.
  // A non-consuming buff (charge) charges up then the attack proceeds
  // this same turn; a consumesTurn buff spends the turn here (no attack) and
  // the multiplier carries to the next swing via pendingAttackMult.
  const buffSkill = resolveBuffSkill(attackerState, skills, rng);
  if (buffSkill) {
    attackerState.pendingAttackMult = buffSkill.attackDamageMultiplier;
    events.push({ type: 'skill_trigger', actor: attacker, skillId: buffSkill.id });
    if (buffSkill.consumesTurn) return;
  }

  const resolution = resolveDefenderReaction(attackerState, defenderState, skills, rng);
  // The swing happened - consume the attacker's buff (decision: a buffed swing
  // that gets dodged/blocked still spends the buff).
  attackerState.pendingAttackMult = 1;

  // --- Emit events in playback order ---

  if (resolution.attackSkill) {
    events.push({ type: 'skill_trigger', actor: attacker, skillId: resolution.attackSkill.id });
  }

  events.push({
    type: 'attack_start',
    actor: attacker,
    defenderReaction: resolution.reaction,
    defaultAttack: resolution.defaultAttack,
  });

  if (resolution.reaction === 'dodge') {
    events.push({ type: 'dodge_success', actor: defender });
    // Defender charged a buff on the dodge (ADBC) - emitted before the counter
    // so the renderer plays the buff anim, then the boosted counter.
    if (resolution.defenderBuffSkill) {
      events.push({ type: 'skill_trigger', actor: defender, skillId: resolution.defenderBuffSkill.id });
      // store the buff so it survives a failed counter roll - contract says
      // the buff lasts until the owner's next damaging swing, whenever it is
      defenderState.pendingAttackMult = resolution.defenderBuffSkill.attackDamageMultiplier;
    }
  } else if (resolution.reaction === 'block' && resolution.blockSkill) {
    events.push({ type: 'skill_trigger', actor: defender, skillId: resolution.blockSkill.id });
    events.push({ type: 'block', actor: defender, reduced: Math.floor(resolution.reduced) });
  }

  // Damage to defender. Skipped on dodge (damage = 0 makes sense, but renderer
  // already short-circuits on amount === 0; we only emit when non-zero hit lands).
  if (resolution.reaction !== 'dodge') {
    // Skill hit -> its own launches flag. Default attack -> kick launches when
    // KICK_LAUNCHES (punch never does).
    const launches = resolution.attackSkill
      ? resolution.attackSkill.launches ?? false
      : resolution.defaultAttack === 'kick' && KICK_LAUNCHES;
    applyDamage(defenderState, resolution.damage, defender, events, launches);
  }

  // Counter-attack hits the original attacker (if still alive). Punch or kick
  // (rolled with the counter); a kick counter launches when KICK_LAUNCHES.
  if (resolution.counter && attackerState.currentHP > 0) {
    const counterVariant: DefaultAttack = resolution.counterKick ? 'kick' : 'punch';
    events.push({ type: 'counter', actor: defender, defaultAttack: counterVariant });
    applyDamage(
      attackerState,
      resolution.counterDamage,
      attacker,
      events,
      resolution.counterKick && KICK_LAUNCHES,
    );
    // counter is a damaging swing - it consumes the defender's buff
    defenderState.pendingAttackMult = 1;
  }

  // Arena interaction hook. Runs between counter and attack_end so any arena
  // events (attach / release + explosion damage) land mid-swing on the renderer.
  if (arenaInteractions?.onAttackResolved) {
    arenaInteractions.onAttackResolved(
      {
        attacker,
        defender,
        attackSkill: resolution.attackSkill,
        reaction: resolution.reaction,
        finalDamage: resolution.damage,
        reducedDamage: resolution.reduced,
        rng,
        emit: (event) => events.push(event),
        dealDamage: (target, amount) =>
          applyDamage(states[target], amount, target, events, false),
        dealDamageBatch: (hits) => {
          // Apply HP to every target first, then emit one combined event so
          // the renderer can play all reactions at the same time.
          const resolved = hits.map(({ target, amount, launches }) => ({
            target,
            amount,
            newHP: subtractHP(states[target], amount),
            launches,
          }));
          if (resolved.length > 0) events.push({ type: 'arena_explode', hits: resolved });
        },
        getHP: (a) => states[a].currentHP,
      },
      arenaState,
    );
  }

  events.push({ type: 'attack_end', actor: attacker });
}

// Pure-ish (consumes rng) - rolls everything an attack needs and reports the
// outcome as a plain object. RNG order is fixed and load-bearing for the dev
// scenario DSL: attack-skill rolls, [default-attack variant roll - ONLY when
// no attack-skill triggered], dodge, [counter] OR [block-skill rolls,
// block-counter]. Don't reorder without updating dev/scenario.ts in lockstep.
function resolveDefenderReaction(
  attackerState: CombatantState,
  defenderState: CombatantState,
  skills: SkillRegistry,
  rng: () => number,
): AttackResolution {
  const attackSkill = resolveAttackSkill(attackerState, skills, rng);

  // Default-attack variant: only rolled when no attack-skill drove the swing
  // (skills have their own multiplier + visual). Kick is rarer and hits harder.
  let defaultAttack: DefaultAttack = 'punch';
  let defaultMult = 1;
  if (!attackSkill) {
    defaultAttack = rng() < KICK_CHANCE ? 'kick' : 'punch';
    if (defaultAttack === 'kick') defaultMult = KICK_DAMAGE_MULT;
  }

  const baseDmg = computeBaseDamage(attackerState.config.stats.strength);
  // pendingAttackMult folds in an active buff (1 when none). resolveAttack
  // resets it after this swing.
  const skillDmg =
    baseDmg * (attackSkill?.damageMultiplier ?? defaultMult) * attackerState.pendingAttackMult;

  // --- Dodge roll ---
  const dodgeChance = computeDodgeChance(defenderState.config.stats.agility);
  const dodged = rng() < dodgeChance;
  if (dodged) {
    // Defender buff rolls AFTER the dodge succeeds, BEFORE the counter roll
    // (ADBC). It boosts the counter and is consumed by it.
    const defenderBuffSkill = resolveBuffSkill(defenderState, skills, rng);
    const counter = rng() < DEFAULT_DODGE_COUNTER_CHANCE;
    // Counter-strike variant: rolled ONLY when a counter actually fires, so
    // scenarios/tests without a counter consume no extra rng.
    const counterKick = counter ? rng() < KICK_CHANCE : false;
    // fresh dodge-buff wins; otherwise a multiplier carried from an earlier
    // consumesTurn buff still applies (counter counts as the next swing)
    const counterMult =
      (defenderBuffSkill
        ? defenderBuffSkill.attackDamageMultiplier
        : defenderState.pendingAttackMult) *
      (counterKick ? KICK_DAMAGE_MULT : 1);
    return {
      attackSkill,
      blockSkill: null,
      defaultAttack,
      reaction: 'dodge',
      damage: 0,
      reduced: 0,
      counter,
      counterKick,
      counterDamage: counter
        ? Math.floor(computeBaseDamage(defenderState.config.stats.strength) * counterMult)
        : 0,
      defenderBuffSkill,
    };
  }

  // --- Block-skill roll (skipped if attack pierces) ---
  const canBlock = !attackSkill?.pierceBlock;
  const blockSkill = canBlock ? resolveBlockSkill(defenderState, skills, rng) : null;
  if (blockSkill) {
    const reducedAmount = skillDmg * blockSkill.blockEffectiveness;
    const finalDmg = Math.floor(skillDmg - reducedAmount);
    const counter = rng() < blockSkill.counterChance;
    const counterKick = counter ? rng() < KICK_CHANCE : false;
    return {
      attackSkill,
      blockSkill,
      defaultAttack,
      reaction: 'block',
      damage: finalDmg,
      reduced: reducedAmount,
      counter,
      counterKick,
      counterDamage: counter
        ? Math.floor(
            computeBaseDamage(defenderState.config.stats.strength) *
              // carried buff applies to a block-counter too
              defenderState.pendingAttackMult *
              (counterKick ? KICK_DAMAGE_MULT : 1),
          )
        : 0,
      defenderBuffSkill: null,
    };
  }

  // --- Plain hit, no skill defense ---
  return {
    attackSkill,
    blockSkill: null,
    defaultAttack,
    reaction: 'hit',
    damage: Math.floor(skillDmg),
    reduced: 0,
    counter: false,
    counterKick: false,
    counterDamage: 0,
    defenderBuffSkill: null,
  };
}

function subtractHP(state: CombatantState, amount: number): number {
  state.currentHP = Math.max(0, state.currentHP - amount);
  return state.currentHP;
}

function applyDamage(
  state: CombatantState,
  amount: number,
  target: Actor,
  events: BattleEvent[],
  launches: boolean,
): void {
  const newHP = subtractHP(state, amount);
  events.push({ type: 'damage', target, amount, newHP, launches });
}

function decideWinner(states: Record<Actor, CombatantState>): Actor | 'draw' {
  const p1Dead = states.p1.currentHP <= 0;
  const p2Dead = states.p2.currentHP <= 0;
  if (p1Dead && p2Dead) return 'draw';
  if (p1Dead) return 'p2';
  if (p2Dead) return 'p1';
  // Both alive: higher HP fraction wins.
  const p1Pct = states.p1.currentHP / states.p1.maxHP;
  const p2Pct = states.p2.currentHP / states.p2.maxHP;
  if (p1Pct > p2Pct) return 'p1';
  if (p2Pct > p1Pct) return 'p2';
  return 'draw';
}
